// src/pages/Login.js
import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAccessibility } from '../contexts/AccessibilityContext';
import AacButton from '../components/aac/AacButton';
import './Login.css';
import { useFocusableNavigation } from '../hooks/useFocusableNavigation';
import { isAnyModalOpen } from '../hooks/useModalDialog';

const isTypingTarget = (el) => {
  if (!el) return false;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
};

// Same document-level number-key pattern AacButton uses, but focuses a
// field instead of clicking a button — lets a switch-scanning user jump
// straight to a field the same way they'd activate a shortcut button.
// Skips while a modal (e.g. the cookie consent banner) is open, so it can't
// pull real focus onto a field hidden behind the modal overlay and break
// the modal's own focus trap.
const useFieldShortcut = (shortcut, fieldRef) => {
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isAnyModalOpen()) return;
      if (isTypingTarget(document.activeElement)) return;
      if (event.key === String(shortcut)) {
        event.preventDefault();
        fieldRef.current?.focus();
        // Re-assert focus on the next frame too. Some AAC switch-scanning
        // software (e.g. Mind Express 5) drives the page by dispatching a
        // synthetic keystroke and then immediately updating its own
        // on-screen scan grid — if that redraw (or anything else reacting
        // to this same keydown) steals focus back in the same tick, a
        // single synchronous .focus() call loses the race silently.
        requestAnimationFrame(() => {
          if (document.activeElement !== fieldRef.current) {
            fieldRef.current?.focus();
          }
        });
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [shortcut, fieldRef]);
};

const Login = () => {
  const { signIn } = useAuth();
  const { announce, showShortcutBadges } = useAccessibility();
  useFocusableNavigation();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  useFieldShortcut(1, emailRef);
  useFieldShortcut(2, passwordRef);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    const { error: signInError } = await signIn(email, password);

    setLoading(false);

    if (signInError) {
      const message = "That email or password isn't right. Please try again.";
      setError(message);
      announce(message);
      return;
    }

    announce('Logged in successfully');
    const destination = location.state?.from?.pathname || '/';
    navigate(destination, { replace: true });
  };

  // The submit button is type="submit" so a real click/Enter/Space already
  // triggers the form's onSubmit natively (required so Enter-in-any-field
  // still submits the form for switch/AAC users). Only the digit-3 AAC
  // shortcut needs to call handleSubmit directly, since it never produces a
  // real click and so never triggers native form submission on its own.
  const handleShortcutSubmit = (event) => {
    if (event.type === 'click') return;
    handleSubmit(event);
  };

  return (
    <div className="auth-page">
      <h1>Log In</h1>
      <p className="auth-page__intro">Log in to see your saved agenda and account.</p>

      <form className="auth-page__form" onSubmit={handleSubmit}>
        <label htmlFor="login-email">Email</label>
        <div className="auth-page__field">
          <input
            id="login-email"
            ref={emailRef}
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
          />
          {showShortcutBadges && (
            <span className="auth-page__field-badge" aria-hidden="true">1</span>
          )}
        </div>

        <label htmlFor="login-password">Password</label>
        <div className="auth-page__field">
          <input
            id="login-password"
            ref={passwordRef}
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            autoComplete="current-password"
          />
          {showShortcutBadges && (
            <span className="auth-page__field-badge" aria-hidden="true">2</span>
          )}
        </div>

        {error && <p className="auth-page__error" role="alert">{error}</p>}

        <div className="auth-page__actions">
          <AacButton type="submit" shortcut={3} onClick={handleShortcutSubmit} disabled={loading}>
            {loading ? 'Logging in…' : 'Log In'}
          </AacButton>

          <AacButton
            type="button"
            variant="secondary"
            shortcut={4}
            onClick={() => setShowPassword((value) => !value)}
          >
            {showPassword ? 'Hide Password' : 'Show Password'}
          </AacButton>
        </div>
      </form>

      <div className="auth-page__links">
        <Link to="/forgot-password">Forgot your password?</Link>
        <Link to="/signup">Don't have an account? Sign up</Link>
      </div>
    </div>
  );
};

export default Login;
